import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  GridLegacy as Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from '@mui/material';

import StagingDataTable from '../StagingDataTable';
import ColumnDefinition from '../../constants/ColumnDefinition';
import PointTypeFilterPopover from '../filter/PointTypeFilterPopover';
import StagingFilterBuilder, { FilterDefinition } from '../StagingFilterBuilder';
import {
  BooleanFilter,
  ContainsTextFilter,
  PointTypeFilter,
} from '../../../../redux/constants/filters';
import FilterContext from '../../contexts/FilterContext';
import BooleanFilterOption from '../filter/BooleanFilterOption';
import TextFilterPopover from '../filter/TextFilterPopover';
import EquipmentDetailBox from '../EquipmentDetailBox';
import UnlinkDialog from './UnlinkDialog';

export const PointSelectorDialog = ({
  open,
  title,
  onSubmit,
  onDemote,
  points,
  buildingId,
  onClose,
  pointTypes,
  equipment,
  deployments,
  promotedEquipmentById,
  hideOrphans,
}) => {
  const { filters, resetFilters } = useContext(FilterContext);
  const [selectedPointTopics, setSelectedPointTopics] = useState([]);
  const [unlinkOpen, setUnlinkOpen] = React.useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const filterDefs = useMemo(
    () => [
      new FilterDefinition(
        BooleanFilterOption,
        'selected_only',
        'Only Selected',
        {},
        () =>
          new BooleanFilter({
            key: 'selected_only',
            getter: (p) => p.topic,
            evaluation: (topic) => selectedPointTopics.includes(topic),
          })
      ),
      new FilterDefinition(
        PointTypeFilterPopover,
        'point_type',
        'Point Type',
        {},
        (type) =>
          new PointTypeFilter({
            key: 'point_type',
            getter: (d) => d.point_type_id,
            type,
          }),
        (type) => type.name
      ),
      new FilterDefinition(
        TextFilterPopover,
        'point_name',
        'Point Name',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'point_name',
            getter: (p) => p.properties?.objectName,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'device_id',
        'Device ID',
        {},
        (v) => new ContainsTextFilter({ key: 'device_id', getter: (p) => p.device_id, fragment: v })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'program_location',
        'Device Program',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'program_location',
            getter: (p) => JSON.stringify(p.device?.properties?.deviceProgramLocation ?? {}),
            fragment: v,
          })
      ),
      hideOrphans,
    ],
    [selectedPointTopics, hideOrphans]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedPointTopics(() => Array.from(equipment.points?.map((p) => p.topic) ?? []));
  }, [equipment]);

  const startingTopicsSet = useMemo(
    () => new Set(Array.from(equipment.points?.map((p) => p.topic) ?? [])),
    [equipment]
  );

  const startingPromotedTopicsSet = useMemo(
    () =>
      new Set(
        Array.from(equipment.points?.filter((p) => p.promoted_point_id).map((p) => p.topic) ?? [])
      ),
    [equipment]
  );

  const removedTopicSet = useMemo(
    () => startingTopicsSet.difference(new Set(selectedPointTopics)),
    [startingTopicsSet, selectedPointTopics]
  );

  const demotedTopicSet = useMemo(
    () => startingPromotedTopicsSet.difference(new Set(selectedPointTopics)),
    [startingPromotedTopicsSet, selectedPointTopics]
  );

  const removedPoints = useMemo(
    () => Array.from(removedTopicSet.values().map((topic) => points.get(topic)) ?? []),
    [removedTopicSet, points]
  );
  const demotedPoints = useMemo(
    () => Array.from(demotedTopicSet.values().map((topic) => points.get(topic)) ?? []),
    [demotedTopicSet, points]
  );

  const handleClose = (resetState) => {
    resetFilters();
    if (resetState) setSelectedPointTopics(Array.from(equipment.points?.map((p) => p.topic) ?? []));
    onClose();
  };

  const onUnlinkDialogClosed = (result) => {
    setUnlinkOpen(false);
    setButtonsDisabled(false);
    if (result) handleClose(false);
  };

  const handleSubmit = () => {
    setButtonsDisabled(true);
    const relatedPointIds = promotedEquipmentById[equipment.promoted_equipment_id]?.pointIds ?? [];
    const demotedPointIds = demotedPoints.map((point) => point.promoted_point_id).filter(Boolean);
    if (demotedPointIds.some((pid) => relatedPointIds.includes(pid))) {
      setUnlinkOpen(true);
    } else {
      onSubmit(selectedPointTopics.map((topic) => points.get(topic))).then(() => {
        onUnlinkDialogClosed(true);
        setButtonsDisabled(false);
      });
    }
  };

  const filteredPoints = useMemo(
    () => Array.from(points.values().filter((point) => filters.every((f) => f.filter(point)))),
    [points, filters]
  );

  const columns = [
    new ColumnDefinition({
      id: 'point_name',
      label: 'Point Name',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.objectName,
    }),
    new ColumnDefinition({
      id: 'point_type',
      label: 'Point Type',
      type: 'text',
      hideable: true,
      render: (point) => (pointTypes ? pointTypes[point.point_type_id]?.name : point.point_type_id),
    }),
    new ColumnDefinition({
      id: 'point_description',
      label: 'Description',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.description,
    }),
    new ColumnDefinition({
      id: 'device_identifier',
      label: 'Device ID',
      type: 'text',
      hideable: true,
      render: (point) => point.device_id ?? point.data['e.device_id'],
    }),
    new ColumnDefinition({
      id: 'deployment_site',
      label: 'Deployment Name',
      type: 'text',
      sortable: true,
      hideable: true,
      defaultHidden: true,
      render: (point) => deployments[point.device.deployment_site],
    }),
    new ColumnDefinition({
      id: 'subdeployment_type',
      label: 'Collection Type',
      type: 'text',
      sortable: true,
      hideable: true,
      defaultHidden: true,
      render: (point) => point.device.subdeployment_type,
    }),
    new ColumnDefinition({
      id: 'object_identifier',
      label: 'Object ID',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.objectIdentifier,
    }),
    new ColumnDefinition({
      id: 'application_software_version',
      label: 'Device Software',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) => point.device?.properties?.applicationSoftwareVersion,
    }),
    new ColumnDefinition({
      id: 'program_location',
      label: 'Device Program',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) =>
        Object.values(point.device?.properties?.deviceProgramLocation ?? {}).join(', '),
    }),
    new ColumnDefinition({
      id: 'object_name',
      label: 'Device Name',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) => point.device.name,
    }),
  ];

  if (points?.length <= 0 || !open) return null;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <UnlinkDialog
          open={unlinkOpen}
          loading={loading}
          demoteEquipment={() => {
            setLoading(true);
            return onDemote(removedTopicSet)
              .then(() => onSubmit(selectedPointTopics.map((topic) => points.get(topic))))
              .then(() => setLoading(false))
              .then(() => onUnlinkDialogClosed(true));
          }}
          removedPoints={removedPoints}
          demotedPoints={demotedPoints}
          onClose={onUnlinkDialogClosed}
        />
        <EquipmentDetailBox equip={equipment} />
        <Grid item xs={12}>
          <StagingFilterBuilder filterDefs={filterDefs} />
        </Grid>
        <Grid item xs={12}>
          <StagingDataTable
            columns={columns}
            allData={points}
            filteredData={filteredPoints}
            rowIdKey="topic"
            loading={loading}
            buildingId={buildingId}
            selectedIds={selectedPointTopics}
            onItemSelected={(newPointTopics) => setSelectedPointTopics(newPointTopics)}
          />
        </Grid>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="success"
          disabled={buttonsDisabled}
        >
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => handleClose(true)}
          color="error"
          disabled={buttonsDisabled}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
